import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadCraftRecipes, loadItems, loadSkills } from '@/data/loader'
import type { CraftRecipe } from '@/types/crafting'
import type { Item } from '@/types/item'
import type { Skill } from '@/types/skill'
import { resolvePublicPath } from '@/utils/assets'
import {
  formatClass,
  formatItemAcquisitionSource,
  formatItemSubType,
  formatItemType,
  formatRarity,
  formatSlotType,
  formatSkillType,
  formatWeaponType,
  shouldShowItemSubType,
} from '@/utils/labels'

type Result =
  | {
    kind: 'item'
    item: Item
    score: number
  }
  | {
    kind: 'skill'
    skill: Skill
    score: number
  }
  | {
    kind: 'recipe'
    recipe: CraftRecipe
    score: number
  }

function normalize(value: string) {
  return (value || '')
    .toLowerCase()
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function scoreText(query: string, text: string) {
  const q = normalize(query)
  const t = normalize(text)
  if (!q || !t) return 0
  const idx = t.indexOf(q)
  if (idx < 0) return 0
  // Prefer prefix matches, then early substring matches.
  return idx === 0 ? 1200 : Math.max(0, 800 - idx * 8)
}

function scoreCandidate(query: string, fields: string[]) {
  let score = 0
  for (const field of fields) {
    score = Math.max(score, scoreText(query, field))
  }
  return score
}

interface Props {
  onClose: () => void
}

export default function SearchModal({ onClose }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [items, setItems] = useState<Item[]>([])
  const [skills, setSkills] = useState<Skill[]>([])
  const [recipes, setRecipes] = useState<CraftRecipe[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    setLoading(true)
    let cancelled = false
    Promise.all([loadItems(), loadSkills(), loadCraftRecipes()])
      .then(([itemsData, skillsData, recipesData]) => {
        if (cancelled) return
        setItems(itemsData)
        setSkills(skillsData)
        setRecipes(recipesData)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setDebouncedQuery(query)
      setSelectedIdx(0)
    }, 250)
    return () => clearTimeout(timerRef.current)
  }, [query])

  const computed = useMemo(() => {
    const q = normalize(debouncedQuery)
    if (q.length < 2) {
      return {
        itemResults: [] as Result[],
        skillResults: [] as Result[],
        recipeResults: [] as Result[],
        flat: [] as Result[],
      }
    }

    const itemResults: Result[] = []
    for (const item of items) {
      const score = scoreCandidate(q, [
        item.name,
        item.subType,
        item.type,
        item.rarity,
        item.class ?? '',
      ])
      if (score <= 0) continue
      const lengthPenalty = Math.min(120, item.name.length) * 0.8
      itemResults.push({ kind: 'item', item, score: score - lengthPenalty })
    }

    const skillResults: Result[] = []
    for (const skill of skills) {
      const score = scoreCandidate(q, [
        skill.name,
        skill.type,
        skill.class ?? '',
        ...(skill.weaponTypes ?? []),
      ])
      if (score <= 0) continue
      const lengthPenalty = Math.min(120, skill.name.length) * 0.8
      skillResults.push({ kind: 'skill', skill, score: score - lengthPenalty })
    }

    const recipeResults: Result[] = []
    for (const recipe of recipes) {
      const score = scoreCandidate(q, [
        recipe.name,
        recipe.slot,
        formatSlotType(recipe.slot),
      ])
      if (score <= 0) continue
      const lengthPenalty = Math.min(120, recipe.name.length) * 0.8
      recipeResults.push({ kind: 'recipe', recipe, score: score - lengthPenalty })
    }

    itemResults.sort((a, b) => b.score - a.score)
    skillResults.sort((a, b) => b.score - a.score)
    recipeResults.sort((a, b) => b.score - a.score)

    const itemsTop = itemResults.slice(0, 8)
    const skillsTop = skillResults.slice(0, 8)
    const recipesTop = recipeResults.slice(0, 8)
    return {
      itemResults: itemsTop,
      skillResults: skillsTop,
      recipeResults: recipesTop,
      flat: [...itemsTop, ...skillsTop, ...recipesTop],
    }
  }, [debouncedQuery, items, recipes, skills])

  function navigateToResult(result: Result) {
    const q = normalize(query)
    const params = new URLSearchParams(location.search)

    if (result.kind === 'item') {
      params.set('tab', 'items')
      if (q) params.set('q', q)
      params.set('open', result.item.id)
      params.delete('cr_open')
      params.delete('s_open')
      const queryString = params.toString()
      navigate(queryString ? `/?${queryString}` : '/')
    } else if (result.kind === 'skill') {
      params.set('tab', 'skills')
      if (q) params.set('s_q', q)
      params.set('s_open', result.skill.id)
      params.delete('cr_open')
      params.delete('open')
      const queryString = params.toString()
      navigate(queryString ? `/?${queryString}` : '/')
    } else {
      params.set('tab', 'crafting')
      if (q) params.set('cr_q', q)
      params.set('cr_open', result.recipe.id)
      params.delete('open')
      params.delete('s_open')
      const queryString = params.toString()
      navigate(queryString ? `/?${queryString}` : '/')
    }

    onClose()
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx((idx) => Math.min(idx + 1, computed.flat.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx((idx) => Math.max(idx - 1, 0))
      return
    }
    if (e.key === 'Enter' && computed.flat[selectedIdx]) {
      navigateToResult(computed.flat[selectedIdx])
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-calamity-panel-dark border border-calamity-accent/30 rounded-2xl shadow-glow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-calamity-border-subtle">
          <svg className="w-5 h-5 text-calamity-accent shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search items, skills, and recipes..."
            className="flex-1 bg-transparent text-sm text-calamity-text placeholder-calamity-text-muted outline-none"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-calamity-panel border border-calamity-border-subtle text-calamity-text-muted">
            ESC
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading && items.length === 0 && skills.length === 0 && recipes.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-calamity-text-dim">
              Loading...
            </div>
          )}

          {!loading && normalize(query).length < 2 && (
            <div className="px-4 py-6 text-center text-sm text-calamity-text-dim">
              Type at least 2 characters to search
            </div>
          )}

          {normalize(query).length >= 2 && computed.flat.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-calamity-text-dim">
              No results found
            </div>
          )}

          {computed.itemResults.length > 0 && (
            <>
              <div className="px-4 pb-2 pt-3 sticky top-0 bg-calamity-panel-dark/95 backdrop-blur-sm z-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-calamity-text-muted">
                  Items
                </div>
              </div>
              {computed.itemResults.map((result, idx) => {
                if (result.kind !== 'item') return null
                const selected = idx === selectedIdx
                const meta = [
                  formatRarity(result.item.rarity),
                  formatItemAcquisitionSource(result.item.acquisitionSource),
                  result.item.class ? formatClass(result.item.class) : '',
                  shouldShowItemSubType(result.item.type, result.item.subType)
                    ? formatItemSubType(result.item.subType)
                    : formatItemType(result.item.type),
                ]
                  .filter(Boolean)
                  .join(' • ')

                return (
                  <button
                    key={`item:${result.item.id}`}
                    type="button"
                    onClick={() => navigateToResult(result)}
                    className={[
                      'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
                      'border-b border-calamity-border-subtle/30 last:border-b-0',
                      selected
                        ? 'bg-calamity-accent/10'
                        : 'hover:bg-calamity-panel-hover',
                    ].join(' ')}
                  >
                    <img
                      src={resolvePublicPath(result.item.icon)}
                      alt={result.item.name}
                      className="h-10 w-10 shrink-0 object-contain pixelated"
                      onError={(event) => {
                        event.currentTarget.src = resolvePublicPath()
                      }}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-calamity-text">
                        {result.item.name}
                      </div>
                      <div className="truncate text-xs text-calamity-text-muted">
                        {meta}
                      </div>
                    </div>
                  </button>
                )
              })}
            </>
          )}

          {computed.skillResults.length > 0 && (
            <>
              <div className="px-4 pb-2 pt-4 sticky top-0 bg-calamity-panel-dark/95 backdrop-blur-sm z-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-calamity-text-muted">
                  Skills
                </div>
              </div>
              {computed.skillResults.map((result, idx) => {
                if (result.kind !== 'skill') return null
                const globalIdx = computed.itemResults.length + idx
                const selected = globalIdx === selectedIdx
                const weapons = (result.skill.weaponTypes ?? [])
                  .slice(0, 2)
                  .map((type) => formatWeaponType(type))
                const meta = [
                  formatSkillType(result.skill.type),
                  result.skill.class ? formatClass(result.skill.class) : 'Universal',
                  weapons.length ? weapons.join(', ') : '',
                ]
                  .filter(Boolean)
                  .join(' • ')

                return (
                  <button
                    key={`skill:${result.skill.id}`}
                    type="button"
                    onClick={() => navigateToResult(result)}
                    className={[
                      'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
                      'border-b border-calamity-border-subtle/30 last:border-b-0',
                      selected
                        ? 'bg-calamity-accent/10'
                        : 'hover:bg-calamity-panel-hover',
                    ].join(' ')}
                  >
                    <img
                      src={resolvePublicPath(result.skill.icon)}
                      alt={result.skill.name}
                      className="h-10 w-10 shrink-0 object-contain pixelated"
                      onError={(event) => {
                        event.currentTarget.src = resolvePublicPath()
                      }}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-calamity-text">
                        {result.skill.name}
                      </div>
                      <div className="truncate text-xs text-calamity-text-muted">
                        {meta}
                      </div>
                    </div>
                  </button>
                )
              })}
            </>
          )}

          {computed.recipeResults.length > 0 && (
            <>
              <div className="px-4 pb-2 pt-4 sticky top-0 bg-calamity-panel-dark/95 backdrop-blur-sm z-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-calamity-text-muted">
                  Crafting
                </div>
              </div>
              {computed.recipeResults.map((result, idx) => {
                if (result.kind !== 'recipe') return null
                const globalIdx = computed.itemResults.length + computed.skillResults.length + idx
                const selected = globalIdx === selectedIdx
                const meta = [
                  formatSlotType(result.recipe.slot),
                  `Tier ${result.recipe.phase}`,
                  `${result.recipe.ingredients.length} materials`,
                ].join(' | ')

                return (
                  <button
                    key={`recipe:${result.recipe.id}`}
                    type="button"
                    onClick={() => navigateToResult(result)}
                    className={[
                      'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
                      'border-b border-calamity-border-subtle/30 last:border-b-0',
                      selected
                        ? 'bg-calamity-accent/10'
                        : 'hover:bg-calamity-panel-hover',
                    ].join(' ')}
                  >
                    <img
                      src={resolvePublicPath(result.recipe.itemIcon)}
                      alt={result.recipe.name}
                      className="h-10 w-10 shrink-0 object-contain pixelated"
                      onError={(event) => {
                        event.currentTarget.src = resolvePublicPath()
                      }}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-calamity-text">
                        {result.recipe.name}
                      </div>
                      <div className="truncate text-xs text-calamity-text-muted">
                        {meta}
                      </div>
                    </div>
                  </button>
                )
              })}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
